import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  apiError,
  isNotFound,
  usePartnerQuery,
  usePriceListQuery,
  useSetCheckoutUrlMutation,
  useSetEmbedOriginsMutation,
  useSetQuotaWarningMutation,
  useSetReturnDomainsMutation,
  useSetWebhookMutation,
  useWebhookDeliveriesQuery,
} from '@/store/api'
import type { PriceKind, WebhookDelivery } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, THead, Th, Tr, Td, SkeletonRows } from '@/components/ui/data-table'
import { EmptyState, ErrorState } from '@/components/ui/empty-state'
import { Term } from '@/components/ui/term'
import { InlineCode } from '@/components/ui/code-block'
import { WhitelabelPanel } from '@/components/panels/WhitelabelPanel'
import { ApiDocsPanel } from './ApiDocs'
import { NotFound } from '@/pages/NotFound'
import { fmtDate, fmtDateTime, fmtMoney, fmtNum } from '@/lib/format'
import { Badge } from '@/components/ui/badge'

const TABS = ['branding', 'checkout', 'embed', 'return', 'webhook', 'quota', 'prices', 'api'] as const
type Tab = (typeof TABS)[number]

const KIND_LABELS: Record<PriceKind, string> = {
  pro: 'Pro',
  pro_upgrade: 'Pro upgrade',
  credit_pack: 'Credit pack',
  website_owner: 'Website Owner plan',
}

export function Settings() {
  const { slug = '', tab } = useParams()
  const navigate = useNavigate()
  const { error } = usePartnerQuery(slug)
  const active: Tab = TABS.includes(tab as Tab) ? (tab as Tab) : 'branding'

  if (isNotFound(error)) return <NotFound />

  return (
    <>
      <PageHeader title="Settings" />
      <Tabs value={active} onValueChange={(v) => navigate(`/p/${slug}/settings/${v}`)}>
        <TabsList className="overflow-x-auto overflow-y-hidden">
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="checkout">Checkout</TabsTrigger>
          <TabsTrigger value="embed">Embed</TabsTrigger>
          <TabsTrigger value="return">Return domains</TabsTrigger>
          <TabsTrigger value="webhook">Webhook</TabsTrigger>
          <TabsTrigger value="quota">Quota warning</TabsTrigger>
          <TabsTrigger value="prices">Price list</TabsTrigger>
          <TabsTrigger value="api">API &amp; keys</TabsTrigger>
        </TabsList>
        <TabsContent value="branding" className="pt-6">
          <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
            How the Atarim experience looks to your End Customers, and who emails to SMB owners come from.
          </p>
          <WhitelabelPanel slug={slug} />
        </TabsContent>
        <TabsContent value="checkout" className="pt-6">
          <CheckoutPanel slug={slug} />
        </TabsContent>
        <TabsContent value="embed" className="pt-6">
          <EmbedOriginsPanel slug={slug} />
        </TabsContent>
        <TabsContent value="return" className="pt-6">
          <ReturnDomainsPanel slug={slug} />
        </TabsContent>
        <TabsContent value="webhook" className="pt-6">
          <WebhookPanel slug={slug} />
        </TabsContent>
        <TabsContent value="quota" className="pt-6">
          <QuotaWarningPanel slug={slug} />
        </TabsContent>
        <TabsContent value="prices" className="pt-6">
          <PriceListPanel slug={slug} />
        </TabsContent>
        <TabsContent value="api" className="pt-6">
          <ApiDocsPanel />
        </TabsContent>
      </Tabs>
    </>
  )
}

function CheckoutPanel({ slug }: { slug: string }) {
  const { data: partner } = usePartnerQuery(slug)
  const [setCheckoutUrl, { isLoading }] = useSetCheckoutUrlMutation()
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setUrl(partner?.checkoutUrl ?? ''), [partner?.checkoutUrl])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await setCheckoutUrl({ slug, checkoutUrl: url.trim() || null }).unwrap()
      toast.success('Checkout URL saved')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <form onSubmit={save} className="max-w-xl space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
      <div>
        <Label htmlFor="checkout-url">Checkout URL</Label>
        <Input
          id="checkout-url"
          type="url"
          placeholder="https://billing.example.com/cart.php?a=add&sku={sku}"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          Where in-app Buy clicks from your customers land in your billing system. You bill your customer; Atarim invoices you the{' '}
          <Term id="expansion-charge">Expansion Charge</Term>. Placeholders: <InlineCode>{'{sku}'}</InlineCode>{' '}
          <InlineCode>{'{credits}'}</InlineCode> (credit packs and SMB budgets) <InlineCode>{'{licence_id}'}</InlineCode> <InlineCode>{'{site_url}'}</InlineCode>{' '}
          <InlineCode>{'{customer_email}'}</InlineCode>. Buy clicks from the product also carry signed{' '}
          <InlineCode>atarim_</InlineCode> parameters with the item, its price and the return URL; see API &amp; keys.
        </p>
      </div>
      <FieldError>{error}</FieldError>
      <Button type="submit" loading={isLoading}>
        Save checkout URL
      </Button>
    </form>
  )
}

function QuotaWarningPanel({ slug }: { slug: string }) {
  const { data: partner } = usePartnerQuery(slug)
  const [setQuotaWarning, { isLoading }] = useSetQuotaWarningMutation()
  const [pct, setPct] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setPct(partner?.quotaWarnPct != null ? String(partner.quotaWarnPct) : ''), [partner?.quotaWarnPct])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await setQuotaWarning({ slug, quotaWarnPct: pct.trim() ? Number(pct) : null }).unwrap()
      toast.success(pct.trim() ? 'Quota warning saved' : 'Quota warning switched off')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <form onSubmit={save} className="max-w-xl space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
      <div>
        <Label htmlFor="quota-warn">Warn at (% of Licence Entitlements in use)</Label>
        <Input id="quota-warn" type="number" min={1} max={100} placeholder="80" value={pct} onChange={(e) => setPct(e.target.value)} />
        <p className="mt-2 text-xs text-muted-foreground">
          Everyone with access to this brand is emailed once a Billing Period when this share of your{' '}
          <Term id="licence-entitlement">Licence Entitlements</Term> is in use. Leave it empty to switch the warning off. The WHMCS module
          sets the same value.
        </p>
      </div>
      <FieldError>{error}</FieldError>
      <Button type="submit" loading={isLoading}>
        Save quota warning
      </Button>
    </form>
  )
}

function EmbedOriginsPanel({ slug }: { slug: string }) {
  const { data: partner } = usePartnerQuery(slug)
  const [save, { isLoading }] = useSetEmbedOriginsMutation()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setText((partner?.embedOrigins ?? []).join('\n')), [partner?.embedOrigins])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const res = await save({ slug, embedOrigins: text.split(/\s+/).filter(Boolean) }).unwrap()
      setText(res.embedOrigins.join('\n'))
      toast.success('Embed domains saved')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <form onSubmit={submit} className="max-w-xl space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
      <div>
        <Label htmlFor="embed-origins">Domains allowed to show the Atarim dashboard</Label>
        <textarea
          id="embed-origins"
          rows={5}
          placeholder={'https://billing.example.com\nhttps://my.example.com'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm focus-visible:ring-2 focus-visible:ring-ring"
        />
        <p className="mt-2 text-xs text-muted-foreground">
          One per line, https only, without a path. Your customers' Atarim dashboard can be shown inside pages on these domains,
          such as your WHMCS client area, and nowhere else.
        </p>
      </div>
      <FieldError>{error}</FieldError>
      <Button type="submit" loading={isLoading}>
        Save domains
      </Button>
    </form>
  )
}

function ReturnDomainsPanel({ slug }: { slug: string }) {
  const { data: partner } = usePartnerQuery(slug)
  const [save, { isLoading }] = useSetReturnDomainsMutation()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setText((partner?.returnDomains ?? []).join('\n')), [partner?.returnDomains])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const res = await save({ slug, returnDomains: text.split(/\s+/).filter(Boolean) }).unwrap()
      setText(res.returnDomains.join('\n'))
      toast.success('Return domains saved')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <form onSubmit={submit} className="max-w-xl space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
      <div>
        <Label htmlFor="return-domains">Domains customers can be sent back to</Label>
        <textarea
          id="return-domains"
          rows={5}
          placeholder={'https://billing.example.com\nhttps://my.example.com'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm focus-visible:ring-2 focus-visible:ring-ring"
        />
        <p className="mt-2 text-xs text-muted-foreground">
          One per line, https only, without a path. When an activation link carries a <InlineCode>redirect_url</InlineCode>, the
          activation page sends the customer back to it only if it is on one of these domains, such as your WHMCS client area:
          straight away if the link is refused, and after onboarding once the licence is claimed. Any other address is ignored.
        </p>
      </div>
      <FieldError>{error}</FieldError>
      <Button type="submit" loading={isLoading}>
        Save domains
      </Button>
    </form>
  )
}

const DELIVERY_BADGE: Record<WebhookDelivery['status'], 'active' | 'pending' | 'revoked'> = {
  delivered: 'active',
  pending: 'pending',
  failed: 'revoked',
}

function WebhookPanel({ slug }: { slug: string }) {
  const { data: partner } = usePartnerQuery(slug)
  const [setWebhook, { isLoading }] = useSetWebhookMutation()
  const deliveries = useWebhookDeliveriesQuery(slug)
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => setUrl(partner?.webhookUrl ?? ''), [partner?.webhookUrl])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await setWebhook({ slug, webhookUrl: url.trim() || null }).unwrap()
      toast.success(url.trim() ? 'Webhook saved' : 'Webhook switched off')
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <>
      <form onSubmit={save} className="max-w-xl space-y-4 rounded-xl border border-border bg-card p-5 shadow-sm">
        <div>
          <Label htmlFor="webhook-url">Webhook URL</Label>
          <Input
            id="webhook-url"
            type="url"
            placeholder="https://billing.example.com/modules/servers/atarim/webhook.php"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Atarim posts <InlineCode>licence.claimed</InlineCode> here when a customer activates a licence, signed with your SSO signing
            key in <InlineCode>X-Atarim-Signature</InlineCode>; see API &amp; keys. Failed deliveries are retried for about a day.
            Leave it empty to switch webhooks off. The WHMCS module sets this for you.
          </p>
        </div>
        <FieldError>{error}</FieldError>
        <Button type="submit" loading={isLoading}>
          Save webhook
        </Button>
      </form>
      <div className="mt-8">
        <h3 className="text-sm font-semibold">Recent deliveries</h3>
        {deliveries.isError ? (
          <ErrorState onRetry={deliveries.refetch} />
        ) : !deliveries.isLoading && deliveries.data?.length === 0 ? (
          <EmptyState title="No deliveries yet" message="Events appear here once a customer activates a licence with a webhook set." />
        ) : (
          <Table>
            <THead>
              <Th>Event</Th>
              <Th>Status</Th>
              <Th right>Attempts</Th>
              <Th>Last response</Th>
              <Th>Sent</Th>
            </THead>
            {deliveries.isLoading ? (
              <SkeletonRows cols={5} rows={3} />
            ) : (
              <tbody>
                {deliveries.data!.map((d) => (
                  <Tr key={d.id}>
                    <Td className="font-mono text-xs">{d.event}</Td>
                    <Td>
                      <Badge variant={DELIVERY_BADGE[d.status]}>{d.status[0].toUpperCase() + d.status.slice(1)}</Badge>
                      {d.nextAttemptAt && (
                        <span className="block text-xs text-muted-foreground">Next try {fmtDateTime(d.nextAttemptAt)}</span>
                      )}
                    </Td>
                    <Td right>{d.attempts}</Td>
                    <Td className="text-muted-foreground">{d.lastError ?? (d.lastResponseStatus ? `Responded ${d.lastResponseStatus}` : '—')}</Td>
                    <Td className="text-muted-foreground">{fmtDateTime(d.deliveredAt ?? d.createdAt)}</Td>
                  </Tr>
                ))}
              </tbody>
            )}
          </Table>
        )}
      </div>
    </>
  )
}

function PriceListPanel({ slug }: { slug: string }) {
  const { data, isLoading, isError, refetch } = usePriceListQuery(slug)
  if (isError) return <ErrorState onRetry={refetch} />

  return (
    <>
      <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
        Atarim's <Term id="list-price">list prices</Term> for your brand{data && ` in ${data.currency}`}. Every{' '}
        <Term id="expansion-charge">Expansion Charge</Term> is 50% of the list price in force when the sale was made, whatever you
        charge your customer. Prices are set by Atarim; contact your Atarim partner manager to discuss them.
      </p>
      {!isLoading && data?.items.length === 0 ? (
        <EmptyState title="No prices set yet" message="Atarim hasn't published a price list for this brand yet." />
      ) : (
        <Table>
          <THead>
            <Th>Product</Th>
            <Th>Type</Th>
            <Th>SKU</Th>
            <Th right>Credits</Th>
            <Th right>List price</Th>
            <Th right>Expansion Charge</Th>
            <Th>Updated</Th>
          </THead>
          {isLoading ? (
            <SkeletonRows cols={7} rows={3} />
          ) : (
            <tbody>
              {data!.items.map((i) => (
                <Tr key={i.id}>
                  <Td className="font-medium">
                    {i.name}
                    {i.band && <span className="block text-xs font-normal text-muted-foreground">{i.band}</span>}
                  </Td>
                  <Td>{KIND_LABELS[i.kind]}</Td>
                  <Td className="font-mono text-xs">{i.sku}</Td>
                  <Td right>{i.credits ? fmtNum(i.credits) : '—'}</Td>
                  <Td right>{fmtMoney(i.amountMinor, data!.currency)}</Td>
                  <Td right>{fmtMoney(Math.round(i.amountMinor / 2), data!.currency)}</Td>
                  <Td className="text-muted-foreground">{fmtDate(i.updatedAt)}</Td>
                </Tr>
              ))}
            </tbody>
          )}
        </Table>
      )}
      {data && data.smbBudgetScale.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold">SMB monthly budgets</h3>
          <p className="mb-3 mt-1 max-w-2xl text-sm text-muted-foreground">
            Sold in 1,000-credit blocks under sku <code className="font-mono text-xs">smb-budget</code>. The whole budget is priced at the
            rate of the highest tier it reaches.
          </p>
          <Table>
            <THead>
              <Th>Budget</Th>
              <Th right>List price per 1,000 credits</Th>
              <Th right>Expansion Charge per 1,000 credits</Th>
            </THead>
            <tbody>
              {data.smbBudgetScale.map((t, i) => {
                const next = data.smbBudgetScale[i + 1]
                return (
                  <Tr key={t.fromCredits}>
                    <Td>
                      {next ? `${fmtNum(t.fromCredits)} – ${fmtNum(next.fromCredits - 1000)} credits` : `${fmtNum(t.fromCredits)}+ credits`}
                    </Td>
                    <Td right>{fmtMoney(t.perThousandMinor, data.currency)}</Td>
                    <Td right>{fmtMoney(Math.round(t.perThousandMinor / 2), data.currency)}</Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      )}
    </>
  )
}
